/**
 * Маршрутизация: карточка дела либо раздел администрирования.
 *
 * Маршрутизатор не подключается сознательно: разделов два, а приложение
 * живёт в iframe карточки — адресная строка ему не принадлежит.
 */

import { useEffect, useState } from 'react';
import { CasePage } from './features/case/CasePage.js';
import { DictionariesPage } from './features/admin/DictionariesPage.js';
import { TemplatesPage } from './features/admin/TemplatesPage.js';
import { AccessPage } from './features/admin/AccessPage.js';
import { api, type Me } from './api.js';
import { initBx24, readCaseContext, type CaseContext } from './bx24.js';
import { useEntityType } from './storage/cases.js';
import { resolveFieldNames, type FieldsCheck } from './storage/diagnostics.js';

/** Завершение установки из приложения — с понятным итогом для полосы вверху. */
async function finishSetup(): Promise<{ ok: boolean; text: string }> {
  try {
    const result = await api.finishSetup();
    if (result.already) return { ok: true, text: 'Приложение установлено.' };
    const failed = result.fields?.failed ?? [];
    const parts = [
      'Установка приложения завершена.',
      result.fields?.created.length ? `Созданы поля сделки: ${result.fields.created.length}.` : '',
      failed.length ? `Не удалось создать: ${failed.map((f) => `«${f.title}»`).join(', ')}.` : '',
      result.bind?.ok ? 'Вкладка «Документы по банкротству» добавлена в карточку сделки.' : `Вкладку привязать не удалось: ${result.bind?.reason ?? 'причина неизвестна'}.`,
    ];
    return { ok: !failed.length && Boolean(result.bind?.ok), text: parts.filter(Boolean).join(' ') };
  } catch (error) {
    return {
      ok: false,
      text: `Установка приложения не завершена: ${(error as Error).message}. Её завершает администратор портала — достаточно, чтобы он открыл приложение.`,
    };
  }
}

type Screen ='case' | 'dictionaries' | 'templates' | 'access';

export function App() {
  const [context, setContext] = useState<CaseContext | null>(null);
  const [screen, setScreen] = useState<Screen>('case');
  const [me, setMe] = useState<Me | null>(null);
  const [fieldsCheck, setFieldsCheck] = useState<FieldsCheck | null>(null);
  const [setupNote, setSetupNote] = useState<{ ok: boolean; text: string } | null>(null);
  const [loading, setLoading] = useState('Подключение к порталу…');

  useEffect(() => {
    void initBx24().then(async () => {
      const ctx = readCaseContext();
      useEntityType(ctx.entityTypeId);

      // Открыто ли приложение этому сотруднику. Сбой ответа не запирает
      // карточку: права всё равно проверяет сервер на каждом запросе
      const who = await api.me().catch(() => null);
      setMe(who);
      if (who && !who.hasAccess) {
        setContext(ctx);
        return;
      }

      // Событие установки до сервера не дошло — завершаем установку отсюда
      // (сервер проверит, что приложение открыл администратор портала)
      if (who && !who.installed) {
        // Портал создаёт поля медленно — у cl24.ru это заняло 38 секунд: без пояснения выглядит как зависание
        setLoading('Завершаю установку приложения: создаю поля сделки и добавляю вкладку. Это может занять до минуты…');
        setSetupNote(await finishSetup());
      }

      // Коды полей сделки присваивает портал: пока они не найдены по названию,
      // профиль писался бы в несуществующее поле, и портал принял бы это молча
      if (!ctx.standalone) setFieldsCheck(await resolveFieldNames(ctx.entityTypeId).catch(() => null));

      setContext(ctx);
      // Вне карточки дела приложение открывается сразу на администрировании
      if (ctx.standalone) setScreen('dictionaries');
    });
  }, []);

  if (!context) return <div className="loading">{loading}</div>;

  // Закрытая установка: сотрудник, которому доступ не открыт, дальше не проходит
  if (me && !me.hasAccess) {
    return (
      <div className="access-denied">
        <h1>Доступ только для администраторов портала</h1>
        <p>Чтобы работать с приложением, попросите администратора портала открыть вам доступ.</p>
      </div>
    );
  }

  const manageAccess = me?.accessMode === 'admins' && me.isPortalAdmin;
  const setupBanner = setupNote && <div className={setupNote.ok ? 'note app-note' : 'banner'}>{setupNote.text}</div>;

  // Портал принимает запись в несуществующее поле молча: без «Профиля дела»
  // введённое пропало бы. Поэтому карточка без него для ввода не открывается
  const noProfileField = Boolean(fieldsCheck?.reachable && fieldsCheck.missing.includes('Профиль дела'));
  const fieldsWarning =
    fieldsCheck?.reachable && (fieldsCheck.missing.length > 0 || fieldsCheck.truncationRisks.length > 0) ? (
      <div className="banner">
        {fieldsCheck.missing.length > 0 && (
          <p>
            В карточке сделки нет служебных полей приложения: {fieldsCheck.missing.map((t) => `«${t}»`).join(', ')}.{' '}
            {noProfileField ? 'Сохранять дело некуда, поэтому ввод закрыт. ' : ''}
            Приложение создаёт эти поля само при установке: попросите администратора портала переустановить приложение
            (Приложения → Мои приложения), либо создать поля вручную по инструкции.
          </p>
        )}
        {fieldsCheck.truncationRisks.map((risk) => (
          <p key={risk}>{risk}</p>
        ))}
      </div>
    ) : null;

  return (
    <div className="app">
      {setupBanner}
      {(context.standalone || screen !== 'case') && (
        <nav className="app-nav">
          {!context.standalone && (
            <button type="button" className={screen === 'case' ? 'active' : ''} onClick={() => setScreen('case')}>
              Карточка дела
            </button>
          )}
          <button type="button" className={screen === 'dictionaries' ? 'active' : ''} onClick={() => setScreen('dictionaries')}>
            Справочники
          </button>
          <button type="button" className={screen === 'templates' ? 'active' : ''} onClick={() => setScreen('templates')}>
            Шаблоны
          </button>
          {manageAccess && (
            <button type="button" className={screen === 'access' ? 'active' : ''} onClick={() => setScreen('access')}>
              Доступ
            </button>
          )}
        </nav>
      )}

      {screen === 'case' && !context.standalone && fieldsWarning}
      {screen === 'case' && !context.standalone && !noProfileField && <CasePage context={context} />}
      {screen === 'case' && context.standalone && (
        <div className="block">
          <h2>Приложение открыто вне карточки дела</h2>
          <p>Откройте сделку и перейдите на вкладку «Документы по банкротству».</p>
        </div>
      )}
      {screen !== 'case' && (
        <div className="admin-page">
          {screen === 'dictionaries' && <DictionariesPage />}
          {screen === 'templates' && <TemplatesPage />}
          {screen === 'access' && manageAccess && <AccessPage />}
        </div>
      )}

      {!context.standalone && screen === 'case' && (
        <footer className="app-footer">
          <button type="button" className="link-button" onClick={() => setScreen('dictionaries')}>
            Справочники
          </button>
          <button type="button" className="link-button" onClick={() => setScreen('templates')}>
            Шаблоны
          </button>
          {manageAccess && (
            <button type="button" className="link-button" onClick={() => setScreen('access')}>
              Доступ
            </button>
          )}
        </footer>
      )}
    </div>
  );
}
